import { apiClient } from '../../utils/apiClient';
import {
    API_TRANSLATIONS_BY_LOCALE,
    API_TRANSLATIONS_BASE,
    API_TRANSLATIONS_BY_ID,
} from '../../constants/apiConstants';

/**
 * DTO representing a single entity-level translation record.
 */
export interface TranslationDto {
    id: number;
    entityType: string;
    entityId: number;
    fieldName: string;
    locale: string;
    value: string;
}

/**
 * Payload for creating a new translation.
 */
export interface CreateTranslationRequest {
    entityType: string;
    entityId: number;
    fieldName: string;
    locale: string;
    value: string;
}

// ── Read API ─────────────────────────────────────────────────────────────────

/**
 * Fetch all translations for a specific entity.
 */
export const fetchTranslationsByEntity = async (
    entityType: string,
    entityId: number
): Promise<TranslationDto[]> => {
    const response = await apiClient.get(API_TRANSLATIONS_BASE, {
        params: { entityType, entityId },
    });
    const result = response.data;
    if (!result.succeeded) {
        throw new Error(result.messages?.join(', ') ?? 'Failed to fetch translations');
    }
    return result.data?.translations ?? [];
};

/**
 * Bulk fetch translations by locale, optionally filtered by entity type.
 */
export const fetchTranslationsByLocale = async (
    locale: string,
    entityType?: string
): Promise<TranslationDto[]> => {
    const params: Record<string, string> = { locale };
    if (entityType) params.entityType = entityType;
    const response = await apiClient.get(API_TRANSLATIONS_BY_LOCALE, { params });
    const result = response.data;
    if (!result.succeeded) {
        throw new Error(result.messages?.join(', ') ?? 'Failed to fetch translations');
    }
    return result.data?.translations ?? [];
};

/**
 * Supported non-English locales for entity translations.
 */
export const TRANSLATION_LOCALES = ['zh-TW', 'zh-CN', 'ja-JP', 'es-ES', 'fr-FR'] as const;

/**
 * Fetch translations for all non-English locales by calling the by-locale
 * endpoint once per locale and merging the results.
 * Optionally filter by entity type.
 */
export const fetchAllTranslations = async (
    entityType?: string
): Promise<TranslationDto[]> => {
    const results = await Promise.all(
        TRANSLATION_LOCALES.map((locale) => fetchTranslationsByLocale(locale, entityType))
    );
    return results.flat();
};

// ── Write API ────────────────────────────────────────────────────────────────

/**
 * Create a new translation record.
 */
export const createTranslation = async (
    req: CreateTranslationRequest
): Promise<TranslationDto> => {
    const response = await apiClient.post(API_TRANSLATIONS_BASE, req);
    const result = response.data;
    if (!result.succeeded) {
        throw new Error(result.messages?.join(', ') ?? 'Failed to create translation');
    }
    const t = result.data?.translation;
    if (!t) throw new Error('Missing translation in response');
    return t;
};

/**
 * Update an existing translation value.
 */
export const updateTranslation = async (
    id: number,
    value: string
): Promise<TranslationDto> => {
    const response = await apiClient.put(API_TRANSLATIONS_BY_ID(id), { value });
    const result = response.data;
    if (!result.succeeded) {
        throw new Error(result.messages?.join(', ') ?? 'Failed to update translation');
    }
    const t = result.data?.translation;
    if (!t) throw new Error('Missing translation in response');
    return t;
};

/**
 * Soft-delete a translation record.
 */
export const deleteTranslation = async (id: number): Promise<void> => {
    const response = await apiClient.delete(API_TRANSLATIONS_BY_ID(id));
    const result = response.data;
    if (!result.succeeded) {
        throw new Error(result.messages?.join(', ') ?? 'Failed to delete translation');
    }
};
